import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import useQueryParams from '../../hooks/useQueryParams';
import {
  fetchKeymakerCurrentUser,
  saveAuthUserDetail,
  verifyJWT,
} from '../../slices/AuthSlice';
import { useAppDispatch } from '../../slices/store';
import { deleteAuthCookie, setAuthCookie } from '../../utils/AuthUtils';
import Logger from '../../utils/Logger';
import { isMalformedURL } from '../../utils/UrlUtils';
import DefaultSplashV2 from '../DefaultSplashV2';
import { getYentaConfiguration } from '../../utils/OpenapiConfigurationUtils';
import { UserControllerApi } from '../../openapi/yenta/api';
import ErrorService from '../../services/ErrorService';
import { showErrorToast } from '../../slices/ToastNotificationSlice';

interface RedirectQuery {
  jwtToken?: string;
  redirectTo?: string;
}

interface RedirectState {
  status: 'processing' | 'ready';
  path: string;
}

interface UserFetchResult {
  success: boolean;
  error?: Error;
}

const ERROR_MESSAGES = {
  KEYMAKER_FETCH: 'Failed to fetch Keymaker user',
  YENTA_FETCH: 'Failed to fetch Yenta user details',
  USER_DETAIL_FETCH: 'Error fetching user detail',
} as const;

const AuthRedirectRoute: React.FC = () => {
  const { jwtToken, redirectTo } = useQueryParams<RedirectQuery>();
  const dispatch = useAppDispatch();
  const [redirectState, setRedirectState] = useState<RedirectState>({
    status: 'processing',
    path: '/login',
  });

  // Memoize the loading screen to prevent re-renders
  const loadingScreen = useMemo(
    () => (
      <div className='h-screen w-screen'>
        <DefaultSplashV2 />
      </div>
    ),
    [],
  );

  // Process URL once when redirectTo changes
  const processedUrl = useMemo(() => {
    if (!redirectTo) return null;

    let url = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;

    if (isMalformedURL(url)) {
      Logger.debug('AuthRedirectRoute: Malformed URL, defaulting to home', url);
      url = '/';
    }

    return url;
  }, [redirectTo]);

  const fetchAndSetUserDetails =
    useCallback(async (): Promise<UserFetchResult> => {
      try {
        Logger.debug('AuthRedirectRoute: Starting user details fetch');
        // Fetch Yenta user details
        const yentaConfig = await getYentaConfiguration();
        const userApi = new UserControllerApi(yentaConfig);
        const { data: userData } = await userApi.getCurrentGenericUser();
        Logger.debug('AuthRedirectRoute: User data fetched', userData);
        dispatch(saveAuthUserDetail(userData));
        await dispatch(fetchKeymakerCurrentUser(true));
        Logger.debug('AuthRedirectRoute: User details saved to Redux');
        return { success: true };
      } catch (error) {
        const errorObj = error as Error;
        Logger.error(ERROR_MESSAGES.USER_DETAIL_FETCH, errorObj);
        ErrorService.notify(ERROR_MESSAGES.USER_DETAIL_FETCH, errorObj);
        dispatch(showErrorToast(ERROR_MESSAGES.USER_DETAIL_FETCH));

        return {
          success: false,
          error: errorObj,
        };
      }
    }, [dispatch]);

  const handleRedirect = useCallback(async () => {
    // Both parameters are required
    if (!jwtToken || !processedUrl) {
      Logger.debug('AuthRedirectRoute: Missing required parameters', {
        hasToken: !!jwtToken,
        hasValidUrl: !!processedUrl,
      });
      setRedirectState({ status: 'ready', path: '/login' });
      return;
    }

    // Validate the JWT token
    const userPrincipal = await dispatch(verifyJWT(jwtToken));

    if (!userPrincipal?.id) {
      Logger.debug('AuthRedirectRoute: Token invalid');
      setRedirectState({ status: 'ready', path: '/login' });
      return;
    }

    // Token is valid - set auth and fetch user details
    Logger.debug(
      'AuthRedirectRoute: Token valid, redirecting to',
      processedUrl,
    );
    deleteAuthCookie();
    setAuthCookie(jwtToken);

    await fetchAndSetUserDetails();

    // Redirect regardless of user fetch result
    // API failures are handled internally and don't block navigation
    Logger.debug('AuthRedirectRoute: Setting redirect to', processedUrl);
    setRedirectState({
      status: 'ready',
      path: processedUrl,
    });
  }, [dispatch, jwtToken, processedUrl, fetchAndSetUserDetails]);

  useEffect(() => {
    handleRedirect();
  }, [handleRedirect]);

  if (redirectState.status === 'processing') {
    return loadingScreen;
  }

  return <Navigate to={redirectState.path} replace />;
};

export default AuthRedirectRoute;
